import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const STATUS_COLOR = {
  PASS: "var(--mq-green)",
  FAIL: "var(--mq-red)",
  DENIED: "var(--mq-yellow)",
};

const REASON_LABELS = {
  ok: { color: "var(--mq-green)", label: "OK" },
  symbol_not_listed: { color: "var(--mq-red)", label: "SYMBOL NOT LISTED" },
  no_liquidity: { color: "var(--mq-yellow)", label: "NO LIQUIDITY" },
  api_error: { color: "var(--mq-red)", label: "API ERROR" },
  timeout: { color: "var(--mq-red)", label: "TIMEOUT" },
  permission_denied: { color: "var(--mq-red)", label: "PERMISSION DENIED" },
};

const fmtTs = (ts) => (ts ? new Date(ts * 1000).toLocaleTimeString() : "—");
const fmtUsd = (n) =>
  (typeof n === "number" && isFinite(n) ? n : 0).toLocaleString("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 2, maximumFractionDigits: 4,
  });

export default function DiagnosticsPanel() {
  const { t } = useI18n();
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [env, setEnv] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [wallets, setWallets] = useState(null);
  const [orderbook, setOrderbook] = useState(null);
  const [crosscheck, setCrosscheck] = useState(null);
  const [lastRunAt, setLastRunAt] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/exchanges");
        const enabled = Array.isArray(r.data?.enabled) ? r.data.enabled : [];
        // Fallback: if no exchange is toggled enabled, expose all supported
        // ones (read-only diagnostics on simulator + any unlinked venue
        // still works for ping / public market data).
        const supported = Array.isArray(r.data?.supported) ? r.data.supported : [];
        const list = enabled.length ? enabled : supported;
        setAvailable(list);
        if (list.length && !selected) {
          setSelected(r.data?.primary || list[0]);
        }
      } catch (err) {
        if (err?.response?.status !== 401) console.warn("exchanges load failed", err?.message);
      }
    })();
  }, []);

  const run = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    setPermissions(null); setWallets(null); setOrderbook(null); setCrosscheck(null);
    try {
      const [envR, permR, wR, obR, ccR] = await Promise.all([
        api.get("/diagnostics/environment"),
        api.get(`/diagnostics/permissions/${selected}`),
        api.get(`/diagnostics/wallets/${selected}`),
        api.get(`/diagnostics/orderbook/${selected}`),
        api.get(`/diagnostics/market-data-crosscheck/${selected}`),
      ]);
      setEnv(envR.data);
      setPermissions(permR.data);
      setWallets(wR.data);
      setOrderbook(obR.data);
      setCrosscheck(ccR.data);
      setLastRunAt(Date.now() / 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || String(err));
    } finally {
      setBusy(false);
    }
  }, [selected]);

  const drift = useMemo(() => {
    if (!env?.clock_drift_per_exchange || !selected) return null;
    return env.clock_drift_per_exchange[selected];
  }, [env, selected]);

  return (
    <div className="mq-panel" data-testid="diagnostics-panel">
      <div className="mq-panel-header">
        <div>
          <span className="mq-heading text-sm font-semibold tracking-wider">
            {t("diagTitle")}
          </span>
          <div className="text-[10px] text-[var(--mq-text-3)]">{t("diagSubtitle")}</div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="diag-exchange-select"
            className="mq-input text-xs"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={available.length === 0}
            style={{ minWidth: 120 }}
          >
            {available.length === 0 && <option>{t("diagNoData")}</option>}
            {available.map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </select>
          <button
            data-testid="diag-run-btn"
            className="mq-btn mq-btn-primary text-xs"
            onClick={run}
            disabled={busy || !selected}
          >
            {busy ? t("diagRunning") : t("diagRunBtn")}
          </button>
        </div>
      </div>

      {/* Environment */}
      {env && (
        <div className="px-4 py-3 border-b text-xs font-mono" style={{ borderColor: "var(--mq-border)" }}
             data-testid="diag-env">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label={t("diagOutboundIp")} value={env.outbound_ip} />
            <Field label={t("diagClockDrift")} value={
              drift?.drift_ms != null
                ? `${drift.drift_ms} ms`
                : (drift?.error || "—")
            } />
            <Field label={t("diagFixieHint")} value="52.87.82.133 / 52.5.155.132" />
          </div>
        </div>
      )}

      {/* Permissions */}
      {permissions && (
        <Section title={t("diagPermissionsTitle")} testid="diag-permissions">
          <div className="mq-table-scroll" data-testid="diag-permissions-scroll">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left mq-label">
                <th className="px-2 py-1">Permission</th>
                <th className="px-2 py-1">{t("diagStepStatus")}</th>
                <th className="px-2 py-1 text-right">{t("diagStepLatency")}</th>
                <th className="px-2 py-1">{t("diagStepCode")}</th>
                <th className="px-2 py-1">{t("diagStepMessage")}</th>
              </tr>
            </thead>
            <tbody>
              {permissions.results.map((r) => (
                <tr key={r.permission} className="border-t" style={{ borderColor: "var(--mq-border)" }}
                    data-testid={`diag-perm-row-${r.permission}`}>
                  <td className="px-2 py-1">{r.permission}</td>
                  <td className="px-2 py-1 font-bold" style={{ color: STATUS_COLOR[r.status] || "var(--mq-text-2)" }}>
                    {r.status}
                  </td>
                  <td className="px-2 py-1 text-right">{r.duration_ms} ms</td>
                  <td className="px-2 py-1">{r.exchange_code || "—"}</td>
                  <td className="px-2 py-1 truncate" style={{ maxWidth: 360 }} title={r.exchange_message}>
                    {r.exchange_message || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Section>
      )}

      {/* Wallets */}
      {wallets && (
        <Section title={t("diagWalletsTitle")} testid="diag-wallets">
          {wallets.error && (
            <div className="px-2 py-1 text-[11px] mb-2"
                 style={{ background: "rgba(255,64,64,0.07)", color: "var(--mq-red)" }}>
              {wallets.error}
            </div>
          )}
          <div className="mq-table-scroll" data-testid="diag-wallets-scroll">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left mq-label">
                <th className="px-2 py-1">{t("diagWalletAsset")}</th>
                <th className="px-2 py-1 text-right">{t("diagWalletAvailable")}</th>
                <th className="px-2 py-1 text-right">{t("diagWalletFrozen")}</th>
                <th className="px-2 py-1 text-right">{t("diagWalletTotal")}</th>
                <th className="px-2 py-1 text-right">{t("diagWalletUsd")}</th>
                <th className="px-2 py-1">{t("diagWalletStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(wallets.balances || {}).length === 0 ? (
                <tr><td colSpan="6" className="px-2 py-3 text-center text-[var(--mq-text-3)]">—</td></tr>
              ) : Object.entries(wallets.balances).map(([asset, b]) => (
                <tr key={asset} className="border-t" style={{ borderColor: "var(--mq-border)" }}
                    data-testid={`diag-wallet-row-${asset}`}>
                  <td className="px-2 py-1">{asset}</td>
                  <td className="px-2 py-1 text-right">{b.free}</td>
                  <td className="px-2 py-1 text-right text-[var(--mq-yellow)]">{b.used}</td>
                  <td className="px-2 py-1 text-right">{b.total}</td>
                  <td className="px-2 py-1 text-right text-[var(--mq-green)]">{fmtUsd(b.usd_value)}</td>
                  <td className="px-2 py-1 text-[10px] truncate" style={{ maxWidth: 220 }} title={b.price_reason}>
                    {b.price_reason ? <span style={{ color: "var(--mq-yellow)" }}>{b.price_reason}</span> : "OK"}
                  </td>
                </tr>
              ))}
            </tbody>
            {wallets.total_usd > 0 && (
              <tfoot>
                <tr className="border-t" style={{ borderColor: "var(--mq-border)" }}>
                  <td colSpan="4" className="px-2 py-1 text-right font-bold">Σ TOTAL USD</td>
                  <td className="px-2 py-1 text-right font-bold text-[var(--mq-green)]">{fmtUsd(wallets.total_usd)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
          </div>
          {lastRunAt > 0 && (
            <div className="text-[10px] text-[var(--mq-text-3)] mt-1">
              {t("diagWalletLastSync")}: {fmtTs(lastRunAt)}
            </div>
          )}
        </Section>
      )}

      {/* Order book */}
      {orderbook && (
        <Section title={t("diagOrderbookTitle")} testid="diag-orderbook">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs font-mono">
            <Field label="Bids" value={String(orderbook.bids)} />
            <Field label="Asks" value={String(orderbook.asks)} />
            <Field label="Top bid" value={String(orderbook.top_bid)} />
            <Field label="Top ask" value={String(orderbook.top_ask)} />
            <Field label="Spread bps" value={String(orderbook.spread_bps)} />
          </div>
          <div className="mt-2 px-2 py-1 text-[11px]" style={{
            background: "rgba(0,0,0,0.2)",
            color: (REASON_LABELS[orderbook.reason] || {}).color || "var(--mq-text-2)",
          }}>
            <strong>{t("diagOrderbookReason")}:</strong> {(REASON_LABELS[orderbook.reason] || {}).label || orderbook.reason}
            {orderbook.detail ? ` — ${orderbook.detail}` : ""}
          </div>
        </Section>
      )}

      {/* Cross-check */}
      {crosscheck && (
        <Section title={t("diagCrosscheckTitle")} testid="diag-crosscheck">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
            <Field label="CCXT last" value={String(crosscheck.ccxt_last)} />
            <Field label="Native last" value={String(crosscheck.native_last)} />
            <Field label="Δ bps" value={crosscheck.diff_bps != null ? String(crosscheck.diff_bps) : "—"} />
            <Field label="Verdict" value={crosscheck.verdict}
                   color={crosscheck.verdict === "match" ? "var(--mq-green)"
                        : crosscheck.verdict === "divergence" ? "var(--mq-red)" : "var(--mq-yellow)"} />
          </div>
          {(crosscheck.ccxt_error || crosscheck.native_error) && (
            <div className="mt-2 text-[10px] text-[var(--mq-yellow)] font-mono">
              {crosscheck.ccxt_error && <div>CCXT: {crosscheck.ccxt_error}</div>}
              {crosscheck.native_error && <div>Native: {crosscheck.native_error}</div>}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="px-4 py-3 border-b" style={{ borderColor: "var(--mq-border)" }} data-testid={testid}>
      <div className="mq-label mb-2 text-[11px]">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, color }) {
  return (
    <div className="flex flex-col">
      <span className="mq-label text-[10px]">{label}</span>
      <span style={{ color: color || "var(--mq-text-1)" }}>{value}</span>
    </div>
  );
}
